/* Mobility Robot: enhance native Motion variants and record a separate cart declaration. */
(() => {
  'use strict';
  const attributeKeys = ['recipient', 'address', 'condition', 'declarant', 'capacity', 'declaration', 'basket', 'goods', 'version', 'date'];
  const reliefLines = items => items.filter(item => (item.options_with_values || []).some(option => option.value === 'VAT Relief'));
  const fingerprint = items => reliefLines(items).map(item => `${item.key}:${item.quantity};`).join('');
  const goodsRecord = items => reliefLines(items).map(item => `${item.product_title || item.title} / ${item.variant_title} × ${item.quantity}; `).join('');

  function initProduct(panel) {
    if (panel.dataset.initialized) return;
    const section = panel.closest('[data-section-type="product"]');
    const index = Number(panel.dataset.vatIndex);
    const native = section && section.querySelector(`select[data-index="option${index + 1}"]`);
    const data = panel.querySelector('[data-mobility-vat-variants]');
    if (!native || !data) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch (_) { return; }
    const radios = [...panel.querySelectorAll('[data-mobility-vat-choice]')];
    const priceLabel = section.querySelector('[data-mobility-vat-label]');
    const message = panel.querySelector('[data-mobility-vat-status]');

    function sync(selectedVariant) {
      const selects = [...section.querySelectorAll('select[data-index^="option"]')];
      const selectedOptions = selects.map(select => select.value);
      const variant = selectedVariant || variants.find(candidate => candidate.options.every((value, position) => value === selectedOptions[position]));
      const choice = variant ? variant.options[index] : native.value;
      radios.forEach(radio => {
        const match = variants.find(candidate => candidate.options.every((value, position) => position === index ? value === radio.value : value === selectedOptions[position]));
        radio.checked = radio.value === choice;
        radio.disabled = !match || !match.available;
        const label = radio.closest('label');
        const price = label.querySelector('[data-mobility-choice-price]');
        if (price) price.textContent = match ? match.priceFormatted : 'Unavailable';
        label.classList.toggle('is-unavailable', !match || !match.available);
      });
      if (priceLabel) priceLabel.textContent = choice === 'VAT Relief' ? 'With VAT relief, if eligible' : 'Including VAT';
      if (message) {
        message.textContent = choice === 'VAT Relief' ? 'Complete your VAT relief declaration in the cart before checkout.' : 'No VAT relief declaration is needed for this option.';
      }
    }
    radios.forEach(radio => radio.addEventListener('change', () => {
      if (!radio.checked) return;
      native.value = radio.value;
      native.dispatchEvent(new Event('change', {bubbles: true}));
    }));
    section.addEventListener('variantChange', event => sync(event.detail && event.detail.variant));
    sync();
    panel.hidden = false;
    const fallback = section.querySelector('[data-mobility-vat-native]');
    if (fallback) fallback.hidden = true;
    panel.dataset.initialized = 'true';
  }

  function initCart(panel) {
    if (panel.dataset.initialized) return;
    const form = panel.closest('form');
    const data = form && form.querySelector('[data-mobility-cart-lines]');
    if (!form || !data) return;
    let items;
    try { items = JSON.parse(data.textContent); } catch (_) { return; }
    const fields = panel.querySelector('[data-mobility-vat-fields]');
    const inputs = [...fields.querySelectorAll('[name]')].filter(input => input.name.startsWith('attributes[MR VAT '));
    const declaration = panel.querySelector('#MobilityVatDeclaration');
    const basketInput = panel.querySelector('[data-mobility-vat-fingerprint]');
    const goodsInput = panel.querySelector('[data-mobility-vat-goods-record]');
    const dateInput = panel.querySelector('[data-mobility-vat-date]');
    const error = panel.querySelector('[data-mobility-vat-error]');
    const status = panel.querySelector('[data-mobility-vat-status]');
    const saveButton = panel.querySelector('[data-mobility-save-declaration]');
    const checkoutButton = form.querySelector('[name="checkout"]');
    let saved = panel.dataset.saved === 'true';
    let saving = false;
    let quantityPending = false;
    let allowSubmit = false;
    let generation = 0;

    function setLineStatus() {
      form.querySelectorAll('[data-mobility-line-vat-status]').forEach(element => {
        element.textContent = saved ? 'VAT relief selected · declaration recorded' : 'VAT relief selected · declaration required';
      });
    }
    function showError(text) {
      error.textContent = text;
      error.hidden = false;
      error.focus();
    }
    function invalidate(text) {
      generation += 1;
      saved = false;
      allowSubmit = false;
      declaration.checked = false;
      error.hidden = true;
      status.textContent = text;
      setLineStatus();
    }
    function syncCart(nextItems) {
      const previous = fingerprint(items);
      items = nextItems;
      const next = fingerprint(items);
      const lines = reliefLines(items);
      panel.hidden = lines.length === 0;
      fields.disabled = lines.length === 0;
      basketInput.value = next;
      goodsInput.value = goodsRecord(items);
      const list = panel.querySelector('[data-mobility-vat-goods]');
      list.replaceChildren(...lines.map(item => {
        const li = document.createElement('li');
        li.textContent = `${item.product_title || item.title} — ${item.variant_title} × ${item.quantity}`;
        return li;
      }));
      if (next !== previous) invalidate('Your VAT Relief items changed. Please review the items above and confirm your declaration again.');
      setLineStatus();
    }
    function validate() {
      if (!reliefLines(items).length) return true;
      for (const input of inputs) {
        if (input.type !== 'hidden' && input.type !== 'checkbox') input.value = input.value.trim();
      }
      const invalid = inputs.find(input => !input.disabled && !input.checkValidity());
      if (invalid) {
        showError('Please complete the declaration details and tick the separate VAT relief declaration before checkout.');
        invalid.focus();
        invalid.reportValidity();
        return false;
      }
      return true;
    }
    function attributes() {
      const result = Object.fromEntries(attributeKeys.map(key => [`MR VAT ${key}`, '']));
      if (reliefLines(items).length) {
        inputs.forEach(input => {
          if (input.type === 'checkbox' && !input.checked) return;
          result[input.name.slice(11, -1)] = input.value;
        });
      }
      return result;
    }
    async function save() {
      if (quantityPending) {
        showError('Your basket is updating. Please wait for the new quantities before confirming your declaration.');
        return false;
      }
      const quantityInputs = [...form.querySelectorAll('.js-qty__num[data-id]')];
      const quantitiesMatch = reliefLines(items).every(item => {
        const input = quantityInputs.find(control => control.dataset.id === item.key);
        return input && /^\d+$/.test(input.value) && Number(input.value) === item.quantity;
      });
      if (!quantitiesMatch) {
        invalidate('Your item quantities changed. Please wait for the basket to update, then confirm your declaration again.');
        showError('Please finish updating your basket before saving the VAT relief declaration.');
        return false;
      }
      if (saving || !validate()) return false;
      const startGeneration = generation;
      const expectedFingerprint = fingerprint(items);
      if (expectedFingerprint && !saved) dateInput.value = new Date().toISOString();
      const expected = attributes();
      const buttons = [saveButton, checkoutButton].filter(Boolean);
      const quantityControls = [...form.querySelectorAll('.js-qty__adjust, .js-qty__num')];
      const previousDisabled = quantityControls.map(control => control.disabled);
      saving = true;
      buttons.forEach(button => { button.disabled = true; });
      quantityControls.forEach(control => { control.disabled = true; });
      error.hidden = true;
      status.textContent = expectedFingerprint ? 'Saving your declaration…' : 'Updating your basket…';
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart/update.js`, {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({attributes: expected})});
        if (!response.ok) throw new Error('save-failed');
        const cart = await response.json();
        if (fingerprint(cart.items) !== expectedFingerprint || startGeneration !== generation) {
          syncCart(cart.items);
          invalidate('Your basket changed while saving. Please review and confirm your declaration again.');
          return false;
        }
        const persisted = cart.attributes || {};
        if (Object.keys(expected).some(key => String(persisted[key] || '') !== expected[key])) throw new Error('save-incomplete');
        saved = Boolean(expectedFingerprint);
        status.textContent = saved ? 'Declaration recorded for these items. You can continue to checkout.' : '';
        setLineStatus();
        return true;
      } catch (_) {
        showError('We could not save your declaration. Please try again before continuing to checkout, or call 020 8050 4849.');
        return false;
      } finally {
        saving = false;
        buttons.forEach(button => { button.disabled = false; });
        quantityControls.forEach((control, index) => { control.disabled = previousDisabled[index]; });
      }
    }
    inputs.forEach(input => {
      if (input.type === 'hidden' || input.type === 'checkbox') return;
      input.addEventListener('input', () => invalidate('Your declaration details changed. Please confirm the declaration again.'));
    });
    declaration.addEventListener('change', () => {
      generation += 1;
      saved = false;
      allowSubmit = false;
      status.textContent = declaration.checked ? 'Ready to save your declaration.' : 'Please confirm the declaration before checkout.';
      setLineStatus();
    });
    form.addEventListener('submit', async event => {
      if (allowSubmit) { allowSubmit = false; return; }
      const submitter = event.submitter;
      if (submitter && submitter !== checkoutButton && submitter !== saveButton) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (await save()) {
        if (submitter === saveButton) return;
        allowSubmit = true;
        form.requestSubmit(checkoutButton);
      }
    }, true);
    // Native browser validation still protects the no-JavaScript form.
    // With JavaScript, our explicit submit handler gives an accessible error summary.
    form.noValidate = true;
    document.addEventListener('cart:quantity.cart-page', event => {
      const key = event.detail && event.detail[0];
      if (!reliefLines(items).some(item => item.key === key)) return;
      quantityPending = true;
      invalidate('Your basket is updating. Please wait for the new quantities, then confirm your declaration again.');
    });
    document.addEventListener('cart:updated', event => {
      if (!event.detail || !event.detail.cart) return;
      if (!Array.isArray(event.detail.cart.items)) {
        if (quantityPending) {
          quantityPending = false;
          invalidate('Your basket could not be updated. Please review the quantities or refresh your basket, then confirm again.');
          showError('We could not update your basket. Please review the quantities before continuing.');
        }
        return;
      }
      quantityPending = false;
      syncCart(event.detail.cart.items);
    });
    const products = form.querySelector('[data-products]');
    if (products) new MutationObserver(setLineStatus).observe(products, {childList: true});
    syncCart(items);
    panel.dataset.initialized = 'true';
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-mobility-vat-product]').forEach(initProduct);
    root.querySelectorAll('[data-mobility-vat-cart]').forEach(initCart);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('variantChange', event => {
    const section = event.target.closest?.('[data-section-type="product"]');
    const label = section?.querySelector('[data-mobility-vat-label]');
    if (!label) return;
    const choice = event.detail?.variant?.options?.[Number(label.dataset.vatIndex)];
    if (choice === 'VAT Relief' || choice === 'Standard') label.textContent = choice === 'VAT Relief' ? 'With VAT relief, if eligible' : 'Including VAT';
  }, true);
})();
